/**
 * WALL auto-cycling reel scheduler (#222).
 *
 * A wall media bank has a fixed number of TILES (`slots`) but the alert-driven
 * selection (`selectWallFeeds`) can surface MORE candidate feeds than tiles. The
 * reel rotates which window ("page") of candidates is on screen on a CALM,
 * configurable dwell — content rotation only, so more feeds than tiles get screen
 * time without any new map motion (Law 6: the only motion budget on the wall is
 * red unacked anomalies + green live cameras).
 *
 * Pure + deterministic (no React / timers / Date.now) so the paging math is
 * unit-testable; the React zone (`WallMediaZone`) owns the dwell timer and feeds
 * this the candidate count + current page.
 *
 * PAUSE rule: when an operator has escalated a target to the wall, the reel
 * freezes on the page showing that target (`shouldAdvanceReel` → false) so the
 * promoted feed stays put until the escalation clears.
 */

/** Default dwell (ms) a reel page stays on screen before rotating. CALM by design
 *  — a wall is GLANCED at over hours, so 120s (#273, design-council PART XI
 *  Ruling 5.1): 12s churn read as restlessness at room scale. `?dwell=N` and a
 *  per-config `reelDwellMs` override it (a demo may shorten). */
export const WALL_REEL_DWELL_MS = 120_000;

/** Cap on candidate depth: a bank surfaces at most `slots × this` feeds, so the
 *  reel cycles through a bounded pool rather than the whole national camera set. */
export const WALL_REEL_MAX_PAGES = 3;

/** Number of pages needed to give every candidate screen time at `slots` tiles. */
export function reelPageCount(total: number, slots: number): number {
  if (slots <= 0 || total <= 0) return 1;
  return Math.max(1, Math.ceil(total / slots));
}

/** The candidate slice visible on `page` (wraps via modulo so an out-of-range page
 *  is always valid). The last page is left-padded to a full window of `slots` when
 *  the candidates don't divide evenly, so the final page is never a lonely sliver
 *  — it back-fills from earlier candidates to keep the mosaic full. */
export function reelWindow<T>(items: readonly T[], page: number, slots: number): T[] {
  if (slots <= 0 || items.length === 0) return [];
  if (items.length <= slots) return items.slice();
  const pages = reelPageCount(items.length, slots);
  const p = ((page % pages) + pages) % pages;
  const start = p * slots;
  const window = items.slice(start, start + slots);
  // Back-fill a short final page from the front so every tile stays filled.
  if (window.length < slots) {
    for (let i = 0; window.length < slots && i < items.length; i += 1) window.push(items[i]!);
  }
  return window;
}

/** Next page index (wraps). Identity when there is nothing to cycle. */
export function advanceReel(page: number, total: number, slots: number): number {
  const pages = reelPageCount(total, slots);
  if (pages <= 1) return 0;
  return (page + 1) % pages;
}

/**
 * Whether the reel timer should advance. It must NOT advance when:
 *   • an escalation has paused the reel (`paused`), or
 *   • there is only one page (all candidates already fit on screen).
 */
export function shouldAdvanceReel(total: number, slots: number, paused: boolean): boolean {
  if (paused) return false;
  return reelPageCount(total, slots) > 1;
}
